import type { UnknownAction } from '@reduxjs/toolkit'
import {
  CREATE_PROTO_SIMULATION,
  CREATING_PROTO_SIMULATION_FAILED,
  CREATING_PROTO_SIMULATION_SUCCEEDED,
  FETCH_SCENARIO,
  FETCH_SCENARIOS,
  FETCHING_SCENARIO_FAILED,
  FETCHING_SCENARIO_SUCCEEDED,
  FETCHING_SCENARIOS_FAILED,
  FETCHING_SCENARIOS_SUCCEEDED,
  INVALIDATE_SCENARIO,
  INVALIDATE_SCENARIO_UDPATE,
  INVALIDATE_SCENARIOS,
  INVALIDATE_SIMULATION_CREATION,
  STOP_SIMULATION,
  UPDATE_SCENARIO,
  UPDATING_SCENARIO_FAILED,
  UPDATING_SCENARIO_SUCCEEDED,
} from "../../actions/actionTypes";

import { Pagination } from 'src/types/common';
import _ from 'lodash';
import { CreateSimulationErrorResponse, CreateSimulationRequest, CreateSimulationResponse } from 'src/types/simulation/simulations';
import { FetchScenariosErrorResponse, FetchScenariosResponse, Scenario, ScenarioRow, UpdateScenarioRequest, UpdateScenarioResponse } from 'src/types/simulation/scenarios';

interface SimulationState {
  creating: boolean;
  item: {
    id: string;
    name: string;
  } | null,
  error?: any;
}

interface ScenariosState {
  loading: boolean;
  items: ScenarioRow[],
  pagination: Pagination,
  error: any,
  scenario: {
    [key: string]: ScenarioState
  },
  update: {
    [key: string]: ScenarioUpdateState
  }
}

interface ScenarioState {
  loading: boolean;
  item: Scenario | null;
  error: any;
  simulation: SimulationState | null;
}

interface ScenarioUpdateState {
  updating: boolean;
  updated: boolean;
  item: Scenario | null;
  error: any;
}

const initialSimulationState: SimulationState = {
  creating: false,
  item: null,
  error: null
}

const initialPaginationState: Pagination = {
  currentPage: -1,
  totalPages: 0,
  totalItems: 0,
  itemsPerPage: 0,
  hasNext: false
}

const initialScenarioState: ScenarioState = {
  loading: false,
  item: null,
  error: null,
  simulation: null,
}

const initialScenarioUpdateState: ScenarioUpdateState = {
  updating: false,
  updated: false,
  item: null,
  error: null,
}

const initialState: ScenariosState = {
  loading: false,
  items: [],
  pagination: initialPaginationState,
  error: null,
  scenario: {},
  update: {}
}



export function scenariosReducer(state: ScenariosState = initialState, action: UnknownAction): ScenariosState {
  switch (action.type) {
    case FETCH_SCENARIOS: {
      return {
        ...state,
        loading: true,
      }
    }

    case FETCHING_SCENARIOS_SUCCEEDED: {
      const { items, pagination } = action.payload as FetchScenariosResponse;
      const existingItems: ScenarioRow[] = state.items;

      let records = items;
      if (pagination.currentPage > 0) {
        records = [...existingItems, ...records];
      }

      records = _.uniqBy(records, 'id');

      return {
        ...state,
        loading: false,
        items: records,
        error: null
      }
    }

    case FETCHING_SCENARIOS_FAILED: {
      const { error } = action.payload as FetchScenariosErrorResponse;
      return {
        ...state,
        loading: false,
        error: error
      }
    }

    case INVALIDATE_SCENARIOS: {
      return {
        ...state,
        loading: false,
        error: null,
        items: [],
        pagination: initialPaginationState,
      }
    }

    case FETCH_SCENARIO: {
      const id = action.payload as string;
      return {
        ...state,
        scenario: {
          ...state.scenario,
          [id]: {
            ...initialScenarioState,
            loading: true
          }
        }
      }
    }

    case FETCHING_SCENARIO_SUCCEEDED: {
      const scenario = action.payload as Scenario;
      const scenarioState = state.scenario[scenario.id];
      if (!scenarioState) {
        return state;
      }

      return {
        ...state,
        scenario: {
          ...state.scenario,
          [scenario.id]: {
            loading: false,
            item: scenario,
            error: null,
            simulation: null
          }
        }
      }
    }

    case FETCHING_SCENARIO_FAILED: {
      const { id, error } = action.payload as any;

      const scenarioState = state.scenario[id];
      if (!scenarioState) {
        return state;
      }

      return {
        ...state,
        scenario: {
          ...state.scenario,
          [id]: {
            loading: false,
            item: null,
            error: error
          }
        }
      }
    }

    case INVALIDATE_SCENARIO: {
      const id = action.payload as string;

      const scenarioState = state.scenario[id];
      if (!scenarioState) {
        return state;
      }

      const updated = _.omit(state.scenario, id);

      return {
        ...state,
        scenario: updated
      }
    }

    case CREATE_PROTO_SIMULATION: {
      const { scenario } = action.payload as CreateSimulationRequest;

      const scenarioState = state.scenario[scenario];
      if (!scenarioState) {
        return state;
      }

      return {
        ...state,
        scenario: {
          ...state.scenario,
          [scenario]: {
            ...scenarioState,
            simulation: {
              ...initialSimulationState,
              creating: true
            }
          }
        }
      }
    }

    case CREATING_PROTO_SIMULATION_SUCCEEDED: {
      const simulation = action.payload as CreateSimulationResponse;
      const scenarioState = state.scenario[simulation.scenario];
      if (!scenarioState) {
        return state;
      }

      return {
        ...state,
        scenario: {
          ...state.scenario,
          [simulation.scenario]: {
            ...scenarioState,
            simulation: {
              ...scenarioState.simulation,
              creating: false,
              item: simulation,
              error: null
            }
          }
        }
      }
    }

    case CREATING_PROTO_SIMULATION_FAILED: {
      const { scenario, error } = action.payload as CreateSimulationErrorResponse;
      const scenarioState = state.scenario[scenario];
      if (!scenarioState) {
        return state;
      }

      return {
        ...state,
        scenario: {
          ...state.scenario,
          [scenario]: {
            ...scenarioState,
            simulation: {
              ...scenarioState.simulation || initialSimulationState,
              creating: false,
              error: error
            }
          }
        }
      }
    }

    case STOP_SIMULATION: {
      const scenario = action.payload as string;
      const scenarioState = state.scenario[scenario];
      if (!scenarioState) {
        return state;
      }

      return {
        ...state,
        scenario: {
          ...state.scenario,
          [scenario]: {
            ...scenarioState,
            simulation: null
          }
        }
      }
    }

    case UPDATE_SCENARIO: {
      const { id } = action.payload as UpdateScenarioRequest;

      return {
        ...state,
        update: {
          ...state.update,
          [id]: {
            ...initialScenarioUpdateState,
            updating: true
          }
        }
      }
    }

    case UPDATING_SCENARIO_SUCCEEDED: {
      const { response } = action.payload as UpdateScenarioResponse;
      const scenarioState = state.update[response.id];
      if (!scenarioState) {
        return state;
      }

      return {
        ...state,
        update: {
          ...state.update,
          [response.id]: {
            ...scenarioState,
            updating: false,
            updated: true,
            item: response
          }
        }
      }
    }

    case UPDATING_SCENARIO_FAILED: {
      const { id, error } = action.payload as any;
      const scenarioState = state.update[id];
      if (!scenarioState) {
        return state;
      }

      return {
        ...state,
        update: {
          ...state.update,
          [id]: {
            ...scenarioState,
            updating: false,
            updated: false,
            error: error
          }
        }
      }
    }

    case INVALIDATE_SCENARIO_UDPATE: {
      const id = action.payload as string;
      const scenarioState = state.update[id];
      if (!scenarioState) {
        return state;
      }

      const updated = _.omit(state.update, id);

      return {
        ...state,
        update: updated
      }
    }

    default: {
      return state
    }
  }
}